// AvailabilityCard — sidebar card surfacing contract end date + the
// player's geographic preferences (player_regional_preferences junction).
//
// Always renders even when sparse — sidebar rhythm is maintained across
// players. Missing values show "Not set" so the user knows the field is
// supported but unfilled rather than absent from the schema.

import { Calendar, MapPin } from 'lucide-react'

interface Props {
  contractEnd: string | null
  contractLengthSought: string | null
  regions: string[]
}

function formatContractEnd(iso: string | null): string {
  if (!iso) return 'Not set'
  // contract_end is text — could be a year ('2026'), a month ('2026-06'),
  // or a full ISO date. Display whatever's most legible.
  const trimmed = iso.trim()
  if (/^\d{4}$/.test(trimmed)) return trimmed
  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    const [y, m] = trimmed.split('-')
    const date = new Date(Number(y), Number(m) - 1, 1)
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
      })
    }
    return trimmed
  }
  const date = new Date(trimmed)
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  return trimmed
}

export default function AvailabilityCard({
  contractEnd,
  contractLengthSought,
  regions,
}: Props) {
  // UX #5 — hide on the read profile when nothing's set. The edit page still
  // exposes the underlying fields.
  if (!contractEnd && !contractLengthSought && regions.length === 0) {
    return null
  }
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-4">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-hoop-accent mb-3">
        Availability
      </h2>

      <div className="space-y-3">
        <div className="flex items-start gap-2 text-xs">
          <Calendar className="w-3.5 h-3.5 text-hoop-text-3 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-hoop-text-3">
              Contract end
            </div>
            <div className="font-medium text-hoop-text">
              {formatContractEnd(contractEnd)}
            </div>
            {contractLengthSought && (
              <div className="text-[11px] text-hoop-text-3 mt-0.5">
                Seeking: {contractLengthSought}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs">
          <MapPin className="w-3.5 h-3.5 text-hoop-text-3 mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-hoop-text-3 mb-1">
              Open regions
            </div>
            {regions.length === 0 ? (
              <div className="text-hoop-text-3">
                Not set
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {regions.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-hoop-bg text-hoop-text-2 border border-hoop-border"
                  >
                    {r}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
